import {APIBase, Fetcher} from "nate-react-api-helpers";
import {Users} from "./Users";
import {Projects} from "./Projects";
import {Openings} from "./Openings";
import {Hardware} from "./Hardware";
import {Companies} from "./Companies";
import {TableConfig} from "./TableConfig";
import {Logistics} from "./Logistics";
import {Print} from "./Print";
import { Pricing } from "./Pricing";
import { Submissions } from "./Submissions";
import {MiscProducts} from "./MiscProducts";
import {Products} from "./Products";
import {ProjectLog} from "./ProjectLog";
import {Library} from "./Library";
import {QuoteOpenings} from "./QuoteOpenings";
import {QuoteGlazing} from "./QuoteGlazing";
import {QuoteHardware} from "./QuoteHardware";
import {QuotePricing} from "./QuotePricing";
import {Keying} from "./Keying";
import {Keying2} from "./Keying2";
import {Div10} from "./Div10";
import {Glazing} from "./Glazing";
import {QuoteDiv10} from "./QuoteDiv10";
import {ElevationTool} from "./ElevationTool";
import {Extractor} from "./Extractor";
import {FeatureFlags} from "./FeatureFlags";
import {Contacts} from "./Contacts";
import {ApiTokens} from "./ApiTokens";
import {ProductCatalogs} from "./ProductCatalogs";
import {ProjectFiles} from "./ProjectFiles";
import {ProjectTimeline} from "./ProjectTimeline";
import {Files} from "./Files";
import {TpLibrary} from "./TpLibrary";
import {TpLibraryTmpl} from "./TpLibraryTmpl";
import {VoiceClips} from "./VoiceClips";
import {QuoteAlternatives} from "./QuoteAlternatives";
import {ProjectChangeset} from "./ProjectChangeset";
import {MiscPricing} from "./MiscPricing";
import {QuoteMiscPricing} from "./QuoteMiscPricing";
import {QuotePriceSectionNotes} from "./QuotePriceSectionNotes";
import {PriceSectionNotes} from "./PriceSectionNotes";
import {Manufacturing} from "./Manufacturing";
import {Receiving} from "./Receiving";
import {Deliveries} from "./Deliveries";
import {ProjectJournal} from "./ProjectJournal";
import {EmailQueue} from "./EmailQueue";
import {ProjectNotes} from "./ProjectNotes";
import {ProjectFreeformChange} from "./ProjectFreeformChange";
import {ProjectWorkOrder} from "./ProjectWorkOrder";
import {Payables} from "./Payables";
import {ProjectSov} from "./ProjectSov";
import {Receivables} from "./Receivables";
import { Util } from "./Util";
import {Report} from "./Report";
import {ConfigureSourcing} from "./ConfigureSourcing";
import {QuoteMiscProducts} from "./QuoteMiscProducts";
import {Tasks2} from "./Tasks2";
import {UserNotifications} from "./UserNotifications";
import {UserPreferences} from "./UserPreferences";
import {DDLOptions} from "./DDLOptions";
import {CompanySettings} from "./CompanySettings";
import {Debug} from "./Debug";
import {LogiCtrl} from "./LogiCtrl";
import {PDFTable} from "./PDFTable";
import {SupplierCategories} from "./SupplierCategories";

class API extends APIBase {
    apiTokens = new ApiTokens(this);
    configureSourcing = new ConfigureSourcing(this);
    contacts = new Contacts(this);
    companies = new Companies(this);
    companySettings = new CompanySettings(this);
    ddlOptions = new DDLOptions(this);
    debug = new Debug(this);
    deliveries = new Deliveries(this)
    div10 = new Div10(this);
    glazing = new Glazing(this);
    elevationTool = new ElevationTool(this);
    emailQueue = new EmailQueue(this);
    extractor = new Extractor(this);
    featureFlags = new FeatureFlags(this);
    files = new Files(this);
    hardware = new Hardware(this);
    hardwareLibrary = new Library(this);
    tpLibrary = new TpLibrary(this);
    tpLibraryTmpl = new TpLibraryTmpl(this);
    keying = new Keying(this);
    keying2 = new Keying2(this);
    logiCtrl = new LogiCtrl(this);
    logistics = new Logistics(this);
    manufacturing = new Manufacturing(this);
    miscPricing = new MiscPricing(this);
    miscProducts = new MiscProducts(this);
    quoteMiscProducts = new QuoteMiscProducts(this);
    openings = new Openings(this);
    payables = new Payables(this);
    priceSectionNotes = new PriceSectionNotes(this)
    pricing = new Pricing(this);
    print = new Print(this);
    productCatalogs = new ProductCatalogs(this);
    products = new Products(this);
    projectChangeset = new ProjectChangeset(this);
    projectFiles = new ProjectFiles(this);
    projectFreeformChange = new ProjectFreeformChange(this);
    projectJournal = new ProjectJournal(this);
    projectLog = new ProjectLog(this);
    projectNotes = new ProjectNotes(this);
    projectTimeline = new ProjectTimeline(this);
    projectWorkOrder = new ProjectWorkOrder(this);
    projectSov = new ProjectSov(this);
    voiceClips = new VoiceClips(this);
    projects = new Projects(this);
    quoteAlternatives = new QuoteAlternatives(this);
    quoteDiv10 = new QuoteDiv10(this);
    quoteGlazing = new QuoteGlazing(this);
    quoteHardware = new QuoteHardware(this);
    quoteMiscPricing = new QuoteMiscPricing(this);
    quoteOpenings = new QuoteOpenings(this);
    quotePriceSectionNotes = new QuotePriceSectionNotes(this)
    quotePricing = new QuotePricing(this);
    report = new Report(this);
    receiving = new Receiving(this);
    receivables = new Receivables(this);
    submission = new Submissions(this);
    supplierCategories = new SupplierCategories(this);
    tableConfig = new TableConfig(this);
    tasks2 = new Tasks2(this);
    users = new Users(this);
    userNotifications = new UserNotifications(this);
    userPreferences = new UserPreferences(this);
    util = new Util(this);
    pdfTable = new PDFTable(this);

    baseUrl = ""; // sometimes used for debugging

    constructor() {
        super({
            jwtRefreshEndpoint: "/api/auth/refresh"
        });

        const handleFetch = this.fetcher.handleFetchResponse;
        this.fetcher.handleFetchResponse = function <T>(this: Fetcher, result: Response, retry?: () => Promise<T>, isRetry?: boolean) {
            assertNotHtml(result.headers.get("Content-Type"), result.url);
            return handleFetch.call(this, result, retry, isRetry) as Promise<T>;
        };

        const handleXhr = this.fetcher.handleXhrResponse;
        this.fetcher.handleXhrResponse = function <T>(this: Fetcher, xhr: XMLHttpRequest, retry?: () => Promise<T>, isRetry?: boolean) {
            assertNotHtml(xhr.getResponseHeader("Content-Type"), xhr.responseURL);
            return handleXhr.call(this, xhr, retry, isRetry) as Promise<T>;
        };

        this.fetcher.preflight.push((input) => {
            const headers = new Headers(input.params.headers);
            headers.set("X-Nonce", window.crypto.randomUUID());
            headers.set("X-Trace", window.crypto.randomUUID());

            const path = `${this.baseUrl}${input.path}`;

            return {
                ...input,
                path,
                params: {
                    ...input.params,
                    headers: headers
                }
            }
        });
    }
}

// a request to an API route the server doesn't have falls through to the react app and
// comes back as index.html with status 200; fail it instead of treating it as a success
function assertNotHtml(contentType: string | null, url: string) {
    if ((contentType || "").indexOf("text/html") !== -1) {
        throw new Error("API route not found: " + url);
    }
}

export const api = new API();